import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './SearchableSelect.css';

export interface SearchableOption {
  value: number;
  label: string;
  subtitle?: string;
}

interface SearchableSelectProps {
  options: SearchableOption[];
  value: number | null;
  onChange: (value: number) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  emptyMessage?: string;
  onCreateNew?: () => void;
  createNewLabel?: string;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  required = false,
  emptyMessage,
  onCreateNew,
  createNewLabel
}) => {
  const { t } = useTranslation();
  const searchPlaceholder = placeholder || t('search');
  const emptyText = emptyMessage || t('noOptionsAvailable');
  const createLabel = createNewLabel || t('createNew');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (option.subtitle && option.subtitle.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          onChange(filteredOptions[highlightedIndex].value);
          setIsOpen(false);
          setSearchTerm('');
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  const handleOptionClick = (optionValue: number) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCreateNew = () => {
    if (onCreateNew) {
      onCreateNew();
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  return (
    <div className="searchable-select-container" ref={containerRef}>
      {label && (
        <label className="searchable-select-label">
          {label} {required && <span className="required">*</span>}
        </label>
      )}

      <div
        className={`searchable-select ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="searchable-select-value">
          {selectedOption ? (
            <div className="selected-option">
              <div className="selected-label">{selectedOption.label}</div>
              {selectedOption.subtitle && (
                <div className="selected-subtitle">{selectedOption.subtitle}</div>
              )}
            </div>
          ) : (
            <span className="placeholder">{searchPlaceholder}</span>
          )}
        </div>
        <div className="searchable-select-arrow">▼</div>
      </div>

      {isOpen && (
        <div className="searchable-select-dropdown">
          <div className="searchable-select-search">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setHighlightedIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="searchable-select-options">
            {filteredOptions.length === 0 && !onCreateNew && (
              <div className="searchable-select-empty">{emptyText}</div>
            )}

            {onCreateNew && (
              <div
                className="searchable-select-option create-new"
                onClick={handleCreateNew}
              >
                {createLabel}
              </div>
            )}

            {filteredOptions.map((option, index) => (
              <div
                key={option.value}
                className={`searchable-select-option ${
                  index === highlightedIndex ? 'highlighted' : ''
                } ${option.value === value ? 'selected' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionClick(option.value);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="option-label">{option.label}</div>
                {option.subtitle && (
                  <div className="option-subtitle">{option.subtitle}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
